import api from './api';

/**
 * DJ Set Service - Track identification and per-track vinyl matching
 *
 * Enables "Dig Deeper" for individual tracks within DJ sets by:
 * 1. Detecting track boundaries using ML model
 * 2. Recognizing tracks via ACRCloud
 * 3. Finding vinyl matches for each track
 */

export type DJSetStatus = 'pending' | 'processing' | 'ready' | 'failed';
export type BoundaryType = 'silence' | 'gapless' | 'transition';

export interface DJSet {
  setId: string;
  streamId: string;
  type: 'DJ_SET';
  status: DJSetStatus;
  createdBy: string;
  audioUrl: string;
  durationMs: number;
  trackCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface TrackSegment {
  segmentId: string;
  setId: string;
  type: 'TRACK_SEGMENT';
  trackNumber: number;
  startTimeMs: number;
  endTimeMs: number;
  durationMs: number;
  boundaryConfidence: number;
  boundaryType: BoundaryType;
  recognized: boolean;
  title?: string;
  artist?: string;
  album?: string;
  releaseYear?: number;
  mood?: string;
  energy?: number;
  bpm?: number;
  key?: string;
  createdAt: number;
  updatedAt: number;
}

export interface DJSetWithTracks {
  set: DJSet;
  tracks: TrackSegment[];
}

export interface TrackAtPosition {
  track: TrackSegment | null;
  nextTrack: TrackSegment | null;
  trackNumber: number;
  totalTracks: number;
}

export interface TrackVinylMatch {
  id: string;
  score: number;
  title: string;
  artist?: string;
  genre?: string;
  coverImage?: string;
  pressYear?: number;
  originalYear?: number;
  // Deep pressing + credit fields (TEM-635) — all optional/defensive.
  // Surfaced from the shared Qdrant `media` payload Record enriches; a given
  // album's point may lack any of these, so consumers must render defensively.
  label?: string;          // e.g. "Blue Note"
  catalogNumber?: string;  // e.g. "BST-84003"
  producer?: string;       // e.g. "Rudy Van Gelder"
  engineer?: string;
  masteredBy?: string;
  country?: string;        // Pressing country
  appSource: string;
}

export interface TrackVinylMatchResponse {
  segmentId: string;
  track: {
    title: string;
    artist?: string;
    recognized: boolean;
  };
  matches: TrackVinylMatch[];
  hasMatches: boolean;
}

export interface ProcessingResult {
  setId: string;
  status: DJSetStatus;
  message: string;
  trackCount?: number;
  tracks?: TrackSegment[];
}

export const djSetService = {
  /**
   * Create a new DJ Set from a stream
   * @param streamId - The stream to analyze
   */
  async createDJSet(streamId: string): Promise<DJSet> {
    return api.post<DJSet>('/dj-sets', { streamId });
  },

  /**
   * Get a DJ Set with all its tracks
   * @param setId - The DJ Set ID
   */
  async getDJSet(setId: string): Promise<DJSetWithTracks> {
    return api.get<DJSetWithTracks>(`/dj-sets/${setId}`);
  },

  /**
   * Get all track segments for a DJ Set
   * @param setId - The DJ Set ID
   */
  async getTrackSegments(setId: string): Promise<{ setId: string; trackCount: number; tracks: TrackSegment[] }> {
    return api.get<{ setId: string; trackCount: number; tracks: TrackSegment[] }>(`/dj-sets/${setId}/tracks`);
  },

  /**
   * Get the track playing at a specific position
   * @param setId - The DJ Set ID
   * @param positionMs - Playback position in milliseconds
   */
  async getTrackAtPosition(setId: string, positionMs: number): Promise<TrackAtPosition> {
    return api.get<TrackAtPosition>(`/dj-sets/${setId}/track-at?positionMs=${positionMs}`);
  },

  /**
   * Trigger track detection and recognition pipeline
   * @param setId - The DJ Set ID to process
   */
  async processDJSet(setId: string, force = false): Promise<ProcessingResult> {
    const query = force ? '?force=true' : '';
    return api.post<ProcessingResult>(`/dj-sets/${setId}/process${query}`, {});
  },

  /**
   * Find vinyl matches for a specific track segment
   * This is the per-track "Dig Deeper" feature
   * @param setId - The DJ Set ID
   * @param segmentId - The track segment ID
   * @param limit - Maximum matches to return
   */
  async findTrackVinylMatches(
    setId: string,
    segmentId: string,
    limit: number = 5
  ): Promise<TrackVinylMatchResponse> {
    return api.get<TrackVinylMatchResponse>(
      `/dj-sets/${setId}/vinyl-match/${segmentId}?limit=${limit}`
    );
  },

  /**
   * Get DJ Set for a stream (check if already analyzed)
   * @param streamId - The stream ID
   */
  async getDJSetByStream(streamId: string): Promise<{
    exists: boolean;
    set?: DJSet;
    tracks?: TrackSegment[];
    streamId: string;
  }> {
    return api.get(`/dj-sets/stream/${streamId}`);
  },

  /**
   * Delete a DJ Set and all its track segments
   * @param setId - The DJ Set ID
   */
  async deleteDJSet(setId: string): Promise<{ deleted: boolean; setId: string }> {
    return api.delete<{ deleted: boolean; setId: string }>(`/dj-sets/${setId}`);
  },

  /**
   * Format duration in milliseconds to human-readable string
   */
  formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  },

  /**
   * Format position as time string (e.g., "2:34" or "1:02:34")
   */
  formatPosition(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};

export default djSetService;
